import { useEffect, useMemo, useRef, useState } from "react";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

import { isSuperAdmin } from "src/Common/function";

function CustomerAddModal({ account, customers, onAddClicked, onUpdateClicked, onCancelClicked }) {
  const customerContainerRef = useRef(null);
  const [name, setName] = useState("");
  const [customerSearch, setCustomerSearch] = useState("");
  const [isCustomerOpen, setIsCustomerOpen] = useState(false);
  const [selectedCustomerIds, setSelectedCustomerIds] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const isEditMode = Boolean(account?.id);
  const canManageCustomerLocations = isSuperAdmin();

  const availableCustomers = useMemo(() => {
    return customers.filter((customer) => {
      if (!customer.account?.id) {
        return true;
      }

      return customer.account.id === account?.id;
    });
  }, [account?.id, customers]);

  const filteredCustomers = useMemo(() => {
    const normalizedSearch = customerSearch.trim().toLowerCase();

    return availableCustomers.filter((customer) => {
      if (!normalizedSearch) {
        return true;
      }

      return [
        customer.companyName,
        customer.contact,
        customer.email,
        customer.address,
        customer.account?.name,
      ]
        .filter(Boolean)
        .some((value) => value.toLowerCase().includes(normalizedSearch));
    });
  }, [availableCustomers, customerSearch]);

  useEffect(() => {
    if (account) {
      setName(account.name || "");
      setSelectedCustomerIds((account.customers || []).map((customer) => customer.id));
    } else {
      setName("");
      setSelectedCustomerIds([]);
    }
  }, [account]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        customerContainerRef.current &&
        !customerContainerRef.current.contains(event.target)
      ) {
        setIsCustomerOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const toggleCustomer = (customerId) => {
    setSelectedCustomerIds((previous) => {
      if (previous.includes(customerId)) {
        return previous.filter((id) => id !== customerId);
      }

      return [...previous, customerId];
    });
  };

  const addClicked = () => {
    setSubmitted(true);

    if (!name.trim()) {
      return;
    }

    const payload = {
      name: name.trim(),
      customerIds: selectedCustomerIds,
    };

    if (isEditMode) {
      onUpdateClicked({
        id: account.id,
        ...payload,
      });
      return;
    }

    onAddClicked(payload);
  };

  return (
    <div
      onClick={onCancelClicked}
      className="fixed w-screen h-screen left-0 top-0 bg-black bg-opacity-30 flex items-center justify-center z-50"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-[560px] bg-white rounded-[6px] p-[18px] flex flex-col"
      >
        <p className="text-[16px] font-OpenSansMedium font-[600] text-titleColor mb-5">
          {isEditMode ? "EDIT ACCOUNT" : "ADD ACCOUNT"}
        </p>

        <div className="flex flex-col justify-between h-full gap-4">
          <div className="h-[60px] w-full">
            <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">
              Name
            </p>
            <div
              className={`w-full h-[30px] border-solid border-[1px] rounded-[5px] flex items-center justify-center px-[15px] ${submitted && !name.trim() ? "border-red-500" : ""
                }`}
            >
              <input
                value={name}
                type="text"
                onChange={(e) => setName(e.target.value)}
                className="w-full text-inputEmailTitle text-[11px] font-[400] font-OpenSansMedium"
                placeholder="Enter account name"
              />
            </div>
          </div>

          {canManageCustomerLocations && (
            <div ref={customerContainerRef} className="relative w-full">
              <p className="text-[12px] font-OpenSansMedium font-[600] text-titleColor mb-2">
                Customer Locations
              </p>
              <div
                onClick={() => setIsCustomerOpen((previous) => !previous)}
                className={`w-full min-h-[36px] border-solid border-[1px] rounded-[5px] flex items-center justify-between px-[15px] cursor-pointer ${
                  submitted && selectedCustomerIds.length === 0 ? "border-borderColor" : ""
                }`}
              >
                <span className="text-[11px] font-[400] font-OpenSansMedium text-inputEmailTitle">
                  {selectedCustomerIds.length > 0
                    ? `${selectedCustomerIds.length} customer location${
                        selectedCustomerIds.length > 1 ? "s" : ""
                      } selected`
                    : "Select customer locations"}
                </span>
                {isCustomerOpen ? <FiChevronUp size={15} /> : <FiChevronDown size={15} />}
              </div>

              {isCustomerOpen && (
                <div className="absolute top-full mt-2 left-0 w-full bg-white border-[1px] border-borderColor rounded-[6px] shadow-lg z-20">
                  <div className="p-3 border-b-[1px] border-borderColor">
                    <input
                      value={customerSearch}
                      onChange={(event) => setCustomerSearch(event.target.value)}
                      placeholder="Search customer locations"
                      className="w-full text-[12px] outline-none"
                    />
                  </div>
                  <div className="max-h-[240px] overflow-y-auto">
                    {filteredCustomers.length === 0 ? (
                      <div className="px-4 py-3 text-[12px] text-subTitleColor">
                        No customer locations found.
                      </div>
                    ) : (
                      filteredCustomers.map((customer) => {
                        const isSelected = selectedCustomerIds.includes(customer.id);

                        return (
                          <label
                            key={customer.id}
                            className="px-4 py-3 flex items-start gap-3 cursor-pointer hover:bg-backgroundColor"
                          >
                            <input
                              type="checkbox"
                              checked={isSelected}
                              onChange={() => toggleCustomer(customer.id)}
                              className="mt-[2px]"
                            />
                            <div className="flex flex-col">
                              <span className="text-[12px] font-OpenSansRegular text-titleColor">
                                {customer.companyName}
                              </span>
                              {customer.account?.name && (
                                <span className="text-[10px] font-OpenSansRegular text-subTitleColor">
                                  Current account: {customer.account.name}
                                </span>
                              )}
                            </div>
                          </label>
                        );
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          )}

          <div className="w-full">
            <div className="flex items-center justify-between gap-4">
              <div
                onClick={onCancelClicked}
                className="w-[50%] h-[30px] text-[10px] rounded-[6px] border-[1px] border-subTitleColor flex items-center justify-center text-subTitleColor cursor-pointer"
              >
                CANCEL
              </div>
              <div
                onClick={addClicked}
                className="w-[50%] h-[30px] text-[10px] rounded-[6px] flex items-center justify-center bg-blueColor text-white cursor-pointer"
              >
                {isEditMode ? "UPDATE" : "ADD"}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default CustomerAddModal;
